/**
 * Goals & KPI sidebar + route nav keys — union of all JWT visibility tiers the user holds.
 * Data visibility remains BE-scoped; this controls menu items and coarse route access only.
 */

import { REPORTS_NAV_VISIBLE } from "./goalsAndKpiConstants";

export const GOALS_KPI_NAV_KEYS = {
  DASHBOARD: "dashboard",
  GOALS: "goals",
  HIERARCHY: "hierarchy",
  PLANS: "plans",
  APPROVALS: "approvals",
  REPORTS: "reports",
  BUDGET: "budget",
  AUDIT: "audit",
  ANALYTICS: "analytics",
  NOTIFICATIONS: "notifications",
  MY_ESCALATIONS: "my_escalations",
  ALERTS_ESCALATIONS: "alerts_escalations",
  TASKS: "tasks",
  MY_STRATEGIC_INITIATIVES: "my_strategic_initiatives",
  MY_AT_RISK_ITEMS: "my_at_risk_items",
  MY_STRATEGIC_OBJECTIVES: "my_strategic_objectives",
  TEAM_TASKS_BUDGET_DETAIL: "team_tasks_budget_detail"
};

/** Shared Department Head sidebar keys — Champion reuses this list so menus stay in sync. */
const DEPT_HEAD_SIDEBAR_NAV = [
  GOALS_KPI_NAV_KEYS.DASHBOARD,
  GOALS_KPI_NAV_KEYS.MY_STRATEGIC_OBJECTIVES,
  GOALS_KPI_NAV_KEYS.REPORTS,
  GOALS_KPI_NAV_KEYS.ANALYTICS,
  GOALS_KPI_NAV_KEYS.NOTIFICATIONS,
  GOALS_KPI_NAV_KEYS.MY_ESCALATIONS
];

/** Sidebar items contributed by each KPI role tier (union when user has multiple roles). */
const TIER_SIDEBAR_NAV = {
  taskOwner: [
    GOALS_KPI_NAV_KEYS.DASHBOARD,
    GOALS_KPI_NAV_KEYS.TASKS,
    GOALS_KPI_NAV_KEYS.NOTIFICATIONS,
    GOALS_KPI_NAV_KEYS.MY_ESCALATIONS
  ],
  divisionHead: [
    GOALS_KPI_NAV_KEYS.DASHBOARD,
    GOALS_KPI_NAV_KEYS.MY_STRATEGIC_INITIATIVES,
    GOALS_KPI_NAV_KEYS.MY_AT_RISK_ITEMS,
    GOALS_KPI_NAV_KEYS.TEAM_TASKS_BUDGET_DETAIL,
    GOALS_KPI_NAV_KEYS.REPORTS,
    GOALS_KPI_NAV_KEYS.ANALYTICS,
    GOALS_KPI_NAV_KEYS.NOTIFICATIONS,
    GOALS_KPI_NAV_KEYS.MY_ESCALATIONS
  ],
  deptHead: DEPT_HEAD_SIDEBAR_NAV,
  // Same menu as Department Head, plus Approvals (Champion/Admin decide surface only).
  champion: [...DEPT_HEAD_SIDEBAR_NAV, GOALS_KPI_NAV_KEYS.APPROVALS],
  ceo: [
    GOALS_KPI_NAV_KEYS.DASHBOARD,
    GOALS_KPI_NAV_KEYS.GOALS,
    GOALS_KPI_NAV_KEYS.HIERARCHY,
    GOALS_KPI_NAV_KEYS.REPORTS,
    GOALS_KPI_NAV_KEYS.BUDGET,
    GOALS_KPI_NAV_KEYS.ANALYTICS,
    GOALS_KPI_NAV_KEYS.NOTIFICATIONS,
    GOALS_KPI_NAV_KEYS.MY_ESCALATIONS,
    GOALS_KPI_NAV_KEYS.ALERTS_ESCALATIONS,
    GOALS_KPI_NAV_KEYS.AUDIT
  ],
  admin: [
    GOALS_KPI_NAV_KEYS.DASHBOARD,
    GOALS_KPI_NAV_KEYS.GOALS,
    GOALS_KPI_NAV_KEYS.HIERARCHY,
    GOALS_KPI_NAV_KEYS.PLANS,
    GOALS_KPI_NAV_KEYS.APPROVALS,
    GOALS_KPI_NAV_KEYS.REPORTS,
    GOALS_KPI_NAV_KEYS.BUDGET,
    GOALS_KPI_NAV_KEYS.ANALYTICS,
    GOALS_KPI_NAV_KEYS.NOTIFICATIONS,
    GOALS_KPI_NAV_KEYS.MY_ESCALATIONS,
    GOALS_KPI_NAV_KEYS.ALERTS_ESCALATIONS,
    GOALS_KPI_NAV_KEYS.AUDIT
  ]
};

const ADMIN_ROUTE_KEYS = new Set(["import", "metadata", "rptbuilder", "admin", "settings", "frameworks"]);

/** Roles that include Reports in their tier nav (task owner excluded). */
const userHasReportsTierAccess = (perms) => {
  if (!perms?.canAccessModule) {
    return false;
  }
  return (
    Boolean(perms.canManageMetadata) ||
    Boolean(perms.canViewAllNodes) ||
    Boolean(perms.canViewDepartmentHierarchy) ||
    Boolean(perms.canViewDivisionHierarchy) ||
    Boolean(perms.canViewChampionHierarchy)
  );
};

/**
 * @param {object} perms — output of buildHierarchyPermissions()
 * @returns {Set<string>} allowed sidebar nav keys
 */
export const getAllowedSidebarNavKeys = (perms) => {
  const keys = new Set();

  if (!perms?.canAccessModule) {
    return keys;
  }

  if (perms.canManageMetadata) {
    TIER_SIDEBAR_NAV.admin.forEach((key) => keys.add(key));
  }
  if (perms.canViewAllNodes) {
    TIER_SIDEBAR_NAV.ceo.forEach((key) => keys.add(key));
  }
  if (perms.canViewDepartmentHierarchy) {
    TIER_SIDEBAR_NAV.deptHead.forEach((key) => keys.add(key));
  }
  if (perms.canViewDivisionHierarchy) {
    TIER_SIDEBAR_NAV.divisionHead.forEach((key) => keys.add(key));
  }
  if (perms.canViewChampionHierarchy) {
    TIER_SIDEBAR_NAV.champion.forEach((key) => keys.add(key));
  }
  if (perms.canViewAssignedHierarchy) {
    TIER_SIDEBAR_NAV.taskOwner.forEach((key) => keys.add(key));
  }

  if (!perms.canViewFullAudit) {
    keys.delete(GOALS_KPI_NAV_KEYS.AUDIT);
  }

  if (!REPORTS_NAV_VISIBLE) {
    keys.delete(GOALS_KPI_NAV_KEYS.REPORTS);
  }

  return keys;
};

/** True when user has VIEW_GOALS_KPI plus at least one KPI visibility tier role. */
export const hasGoalsKpiNavAccess = (perms) =>
  getAllowedSidebarNavKeys(perms).size > 0;

export const canAccessSidebarNavKey = (navKey, perms) =>
  getAllowedSidebarNavKeys(perms).has(navKey);

/**
 * @param {string} routeKey — first segment after /goals_kpi/
 * @param {object} perms — hierarchyPerms from context / hook
 */
export const canAccessGoalsKpiRoute = (routeKey, perms) => {
  if (!hasGoalsKpiNavAccess(perms)) {
    return false;
  }

  const key = routeKey?.split("/")[0] || routeKey;
  const allowed = getAllowedSidebarNavKeys(perms);

  if (ADMIN_ROUTE_KEYS.has(key)) {
    return Boolean(perms.canManageMetadata);
  }

  if (key === "audit") {
    return allowed.has(GOALS_KPI_NAV_KEYS.AUDIT) && Boolean(perms.canViewFullAudit);
  }

  if (key === "plans" || key === "plan") {
    return allowed.has(GOALS_KPI_NAV_KEYS.PLANS);
  }

  if (key === "goals") {
    return allowed.has(GOALS_KPI_NAV_KEYS.GOALS);
  }

  if (key === "hierarchy") {
    return allowed.has(GOALS_KPI_NAV_KEYS.HIERARCHY);
  }

  if (key === "goal" || key === "kpi" || key === "level") {
    return (
      allowed.has(GOALS_KPI_NAV_KEYS.GOALS) ||
      allowed.has(GOALS_KPI_NAV_KEYS.HIERARCHY) ||
      allowed.has(GOALS_KPI_NAV_KEYS.APPROVALS) ||
      Boolean(perms.canViewDepartmentHierarchy) ||
      Boolean(perms.canViewDivisionHierarchy) ||
      Boolean(perms.canViewAssignedHierarchy) ||
      Boolean(perms.canViewChampionHierarchy)
    );
  }

  if (key === "tasks") {
    return allowed.has(GOALS_KPI_NAV_KEYS.TASKS);
  }

  if (key === "task") {
    return (
      allowed.has(GOALS_KPI_NAV_KEYS.DASHBOARD) ||
      allowed.has(GOALS_KPI_NAV_KEYS.APPROVALS) ||
      allowed.has(GOALS_KPI_NAV_KEYS.GOALS) ||
      allowed.has(GOALS_KPI_NAV_KEYS.HIERARCHY) ||
      Boolean(perms.isTaskOwnerOnlyScope)
    );
  }

  if (key === "approvals") {
    return allowed.has(GOALS_KPI_NAV_KEYS.APPROVALS);
  }

  if (key === "reports") {
    return userHasReportsTierAccess(perms);
  }

  if (key === "budget") {
    return allowed.has(GOALS_KPI_NAV_KEYS.BUDGET);
  }

  if (key === "analytics") {
    return allowed.has(GOALS_KPI_NAV_KEYS.ANALYTICS);
  }

  if (key === "notifications") {
    return allowed.has(GOALS_KPI_NAV_KEYS.NOTIFICATIONS);
  }

  if (key === "my_escalations") {
    return allowed.has(GOALS_KPI_NAV_KEYS.MY_ESCALATIONS);
  }

  // Recipients can open a history row they were sent; Admin/CEO can open any row they can list.
  if (key === "alert_history_detail") {
    return (
      allowed.has(GOALS_KPI_NAV_KEYS.ALERTS_ESCALATIONS) ||
      allowed.has(GOALS_KPI_NAV_KEYS.MY_ESCALATIONS)
    );
  }

  if (key === "alerts_escalations") {
    return allowed.has(GOALS_KPI_NAV_KEYS.ALERTS_ESCALATIONS);
  }

  if (key === "dashboard" || key === "") {
    return allowed.has(GOALS_KPI_NAV_KEYS.DASHBOARD);
  }

  if (key === "my_strategic_initiatives") {
    return allowed.has(GOALS_KPI_NAV_KEYS.MY_STRATEGIC_INITIATIVES);
  }

  if (key === "my_at_risk_items") {
    return allowed.has(GOALS_KPI_NAV_KEYS.MY_AT_RISK_ITEMS);
  }

  if (key === "my_strategic_objectives") {
    return allowed.has(GOALS_KPI_NAV_KEYS.MY_STRATEGIC_OBJECTIVES);
  }

  if (key === "team_tasks_budget_detail") {
    return allowed.has(GOALS_KPI_NAV_KEYS.TEAM_TASKS_BUDGET_DETAIL);
  }

  return false;
};

export const resolveGoalsKpiRouteKey = (pathname) => {
  const match = pathname.match(/\/goals_kpi\/?([^/?#]*)/);
  return match?.[1] || "dashboard";
};
